import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import DirectionsTransitIcon from '@mui/icons-material/DirectionsTransit';
import FlightIcon from '@mui/icons-material/Flight';
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus';
import TramIcon from '@mui/icons-material/Tram';
import DirectionsBikeIcon from '@mui/icons-material/DirectionsBike';
import ElectricCarIcon from '@mui/icons-material/ElectricCar';
import DirectionsWalkIcon from '@mui/icons-material/DirectionsWalk';

export interface AddressInfo {
  id: number;
  label: string;
  street: string;
  postalCode: string;
  city: string;
  region: string;
  country: string;
  lat: number;
  lon: number;
}

const getCity = (address: any): string => {
  return address.city || address.town || address.village || address.hamlet || '';
};

const getRegion = (address: any): string => {
  return address.state || address.region || address.county || '';
};

const getStreet = (address: any): string => {
  return address.house_number && address.road
    ? `${address.house_number} ${address.road}`
    : address.road || '';
};

const parseAddress = (item: any): AddressInfo => ({
  id: item.osm_id,
  label: item.display_name,
  lat: parseFloat(item.lat),
  lon: parseFloat(item.lon),
  street: getStreet(item.address),
  postalCode: item.address.postcode || '',
  city: getCity(item.address),
  region: getRegion(item.address),
  country: item.address.country || '',
});

export const getAddressLabel = (address: AddressInfo | null): string => {
  if (!address) return "";
  const parts = [
      address.street,         
      address.postalCode,
      address.city,      
      address.region,          
      address.country,         
    ].filter(Boolean); 
  return parts.join(', ');
}

export const searchAddress = async (query: string): Promise<AddressInfo[]> => {
  const res = await fetch(
    `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
      query,
    )}&addressdetails=1&namedetails=1`,
  );
  const data = await res.json();
  return data.map(parseAddress);
};

export const fetchAddressById = async (
  id: string,
): Promise<AddressInfo | null> => {
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/lookup?format=json&osm_ids=R${id}`,
    );
    const data = await res.json();
    const item = data[0];
    return item ? parseAddress(item) : null;
  } catch (err) {
    console.error('Failed to lookup address:', err);
    return null;
  }
};

export enum TransportMode {
  Car = 'car',
  Transit = 'transit',
  Plane = 'plane',
  Bus = 'bus',
  Metro = 'metro',
  Bike = 'bike',
  Walk = 'foot',
  ElectricCar = 'electric_car',
}

const getOSRMDistance = async (
  mode: 'car' | 'bike' | 'foot',
  address1: AddressInfo,
  address2: AddressInfo,
): Promise<number | null> => {
  try {
    const response = await fetch(
      `https://router.project-osrm.org/route/v1/${mode}/${address1.lon},${address1.lat};${address2.lon},${address2.lat}?overview=false`,
    );
    const data = await response.json();
    return data.routes?.[0]?.distance / 1000 || null;
  } catch (err) {
    console.error(`Failed to get distance from OSRM (${mode}):`, err);
    return null;
  }
};

const toRadians = (deg: number) => (deg * Math.PI) / 180;

const getDistanceByPlane = (
  address1: AddressInfo,
  address2: AddressInfo,
): number => {
  const R = 6371; // Earth's radius in km
  const dLat = toRadians(address2.lat - address1.lat);
  const dLon = toRadians(address2.lon - address1.lon);
  const lat1 = toRadians(address1.lat);
  const lat2 = toRadians(address2.lat);

  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.sin(dLon / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

export const getDistance = async (
  address1: AddressInfo | null,
  address2: AddressInfo | null,
  transport: TransportMode | null,
): Promise<number | null> => {
  if (!address1 || !address2 || !transport) return null;

  switch (transport) {
    case TransportMode.Car:
    case TransportMode.Transit:
    case TransportMode.Bus:
    case TransportMode.Metro:
    case TransportMode.ElectricCar:
      return await getOSRMDistance(TransportMode.Car, address1, address2);
    case TransportMode.Bike:
      return await getOSRMDistance(TransportMode.Bike, address1, address2);
    case TransportMode.Walk:
      return await getOSRMDistance(TransportMode.Walk, address1, address2);
    case TransportMode.Plane:
      return getDistanceByPlane(address1, address2);
    default:
      return null;
  }
};

export const transportOptions = [
  { label: 'Marche', value: TransportMode.Walk, icon: <DirectionsWalkIcon /> },
  { label: 'Vélo', value: TransportMode.Bike, icon: <DirectionsBikeIcon /> },
  { label: 'Métro / Tram', value: TransportMode.Metro, icon: <TramIcon /> },
  { label: 'Bus / Autocar', value: TransportMode.Bus, icon: <DirectionsBusIcon /> },
  { label: 'Train', value: TransportMode.Transit, icon: <DirectionsTransitIcon /> },
  { label: 'Voiture électrique', value: TransportMode.ElectricCar, icon: <ElectricCarIcon /> },
  { label: 'Voiture', value: TransportMode.Car, icon: <DirectionsCarIcon /> },
  // { label: 'Avion', value: TransportMode.Plane, icon: <FlightIcon /> },
];
